import type React from "react";
import { cva, type VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import { Icon, type GlyphName, type IconColor } from "./icon";

const iconTileVariants = cva(
  // The fill is foreground alpha, not the opaque `muted` token: dark `muted`
  // sits BELOW `card`, so an opaque well on a card reads as an inset (or
  // vanishes). Surface-relative alpha keeps the tile a touch lighter than any
  // dark host and a touch darker than any light one — same reasoning as the
  // translucent dark border and the Switch track.
  "flex shrink-0 items-center justify-center bg-foreground/5 dark:bg-foreground/8",
  {
    // The radius scales with the tile so the corner reads proportionate:
    // md on the small rungs, lg on the 32px identity rung.
    variants: {
      size: {
        // 24px — the compact title identity rung.
        sm: "size-6 rounded-md",
        // 28px — the leading glyph of a name/description table cell.
        default: "size-7 rounded-md",
        // 32px — the sidebar/pane identity rung (16px glyph).
        lg: "size-8 rounded-lg",
      },
      bordered: {
        true: "border",
        false: "",
      },
    },
    defaultVariants: { bordered: false, size: "default" },
  },
);

export type IconTileProps = Omittable<{
  /** The glyph. */
  name: GlyphName;
  /** Tile edge: `sm` = 24px, default 28px, `lg` = 32px. The glyph is 14px,
   *  stepping to 16px on `lg`. */
  size?: VariantProps<typeof iconTileVariants>["size"];
  /** Hairline border around the tile. */
  bordered?: VariantProps<typeof iconTileVariants>["bordered"];
  /** Glyph ink; omitted, it inherits the surrounding text color — the
   *  identity-mark treatment, which must stay neutral rather than pick up a
   *  themed accent. */
  color?: IconColor;
  className?: string;
}>;

/**
 * A glyph seated in a soft muted tile — the identity mark that leads a
 * dialog title or a table cell's name. The tile carries the fill (and
 * optional hairline); the glyph is a plain `Icon`, so `color` takes the
 * same ink names.
 */
export function IconTile({
  name,
  size,
  bordered,
  color,
  className,
}: IconTileProps): React.ReactElement {
  return (
    <div className={cn(iconTileVariants({ bordered, size }), className)}>
      <Icon color={color} name={name} size={size === "lg" ? "md" : "sm"} />
    </div>
  );
}
